import { http } from '@/lib/http';

export type BillingCycle = 'monthly' | 'annual';

/**
 * Request body for `POST /businesses/{business_id}/billing/checkout-session`
 * — mirrors `controller.createCheckoutSessionRequest` in the vizitpro-api
 * swagger spec.
 */
export interface CreateCheckoutSessionRequest {
  tier_id: string;
  billing_cycle: BillingCycle;
}

export interface CheckoutSessionDto {
  checkout_url: string;
}

export interface PortalSessionDto {
  portal_url: string;
}

export interface ConnectOnboardingLinkDto {
  onboarding_url: string;
}

/**
 * Calls `POST /businesses/{business_id}/billing/checkout-session`.
 * Owner-only. Returns a URL to Stripe's hosted Checkout page — redirect the
 * browser there (`window.location.href = checkout_url`), no Stripe.js needed.
 */
export async function createCheckoutSession(
  businessId: string,
  body: CreateCheckoutSessionRequest,
): Promise<CheckoutSessionDto> {
  const { data } = await http.post<CheckoutSessionDto>(
    `/businesses/${businessId}/billing/checkout-session`,
    body,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/billing/portal-session`. Owner-only.
 * Returns a URL to Stripe's hosted Customer Portal — redirect the browser
 * there. 400s if the business hasn't completed Checkout yet, so callers
 * should only offer this once `stripe_customer_id` is non-null.
 */
export async function createPortalSession(
  businessId: string,
): Promise<PortalSessionDto> {
  const { data } = await http.post<PortalSessionDto>(
    `/businesses/${businessId}/billing/portal-session`,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/billing/connect-onboarding-link`.
 * Owner-only. Starts (or resumes) Stripe Connect Express onboarding for the
 * business, creating its connected account on first call. Returns a URL to
 * redirect the owner to (`window.location.href = onboarding_url`) — once
 * onboarding completes, the business can offer Stripe as an invoice payment
 * method (reflected by `stripe_connect_charges_enabled` on the business
 * record once the Connect webhook lands).
 */
export async function createConnectOnboardingLink(
  businessId: string,
): Promise<ConnectOnboardingLinkDto> {
  const { data } = await http.post<ConnectOnboardingLinkDto>(
    `/businesses/${businessId}/billing/connect-onboarding-link`,
  );
  return data;
}
